/**
 * Video Upload Service
 *
 * GCS Resumable Uploadを使用した動画アップロードサービス。
 * グローバルで動作し、画面遷移してもアップロードが継続される。
 */

import { auth } from '../config/firebase';
import type {
  UploadResumable,
  VideoUploadCompleteResponse,
  VideoUploadInitResponse,
} from '../types';
import { getApiUrl } from '../utils/api';
import { listenToProcessingStatus } from './statusListener';

// アップロードの進捗コールバック型
export type UploadProgressCallback = (uploadedBytes: number, totalBytes: number) => void;

// アップロード完了コールバック型
export type UploadCompleteCallback = (itemId: string, fileUrl: string) => void;

// アップロードエラーコールバック型
export type UploadErrorCallback = (itemId: string, error: Error) => void;

// アップロードコールバックのセット
export interface UploadCallbacks {
  onProgress?: UploadProgressCallback;
  onComplete?: UploadCompleteCallback;
  onError?: UploadErrorCallback;
}

// アップロードジョブの状態
interface UploadJob {
  itemId: string;
  file: File;
  uploadUri: string;
  blobName: string;
  uploadedBytes: number;
  totalBytes: number;
  abortController: AbortController;
  status: 'uploading' | 'paused' | 'completed' | 'error';
  error?: Error;
  callbacks?: UploadCallbacks;
}

// チャンクサイズ: 8MB (GCS推奨の256KBの倍数)
const CHUNK_SIZE = 8 * 1024 * 1024;

// グローバルなアップロードジョブ管理
const uploadJobs: Map<string, UploadJob> = new Map();

/**
 * アップロードコールバックを更新（画面遷移時の再接続用）
 */
export function updateUploadCallbacks(itemId: string, callbacks: UploadCallbacks | null): void {
  const job = uploadJobs.get(itemId);
  if (job) {
    job.callbacks = callbacks || undefined;
  }
}

/**
 * 現在のアップロードジョブを取得
 */
export function getUploadJob(itemId: string): UploadJob | undefined {
  return uploadJobs.get(itemId);
}

/**
 * すべてのアップロードジョブを取得
 */
export function getAllUploadJobs(): Map<string, UploadJob> {
  return new Map(uploadJobs);
}

/**
 * 動画アップロードを初期化
 * GCS Resumable Upload URIを取得
 */
export async function initializeVideoUpload(
  itemId: string,
  file: File,
): Promise<VideoUploadInitResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(getApiUrl('/api/video/init-upload'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      itemId,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to initialize upload: ${response.status}`);
  }

  return await response.json();
}

/**
 * 動画アップロードを開始
 */
export async function startVideoUpload(
  itemId: string,
  file: File,
  uploadUri: string,
  blobName: string,
  resumeFromBytes: number = 0,
  callbacks?: UploadCallbacks,
): Promise<void> {
  // 既存のジョブがあればキャンセル
  const existingJob = uploadJobs.get(itemId);
  if (existingJob && existingJob.status === 'uploading') {
    existingJob.abortController.abort();
  }

  const abortController = new AbortController();
  const job: UploadJob = {
    itemId,
    file,
    uploadUri,
    blobName,
    uploadedBytes: resumeFromBytes,
    totalBytes: file.size,
    abortController,
    status: 'uploading',
    callbacks,
  };

  uploadJobs.set(itemId, job);

  try {
    await uploadChunks(job);

    // アップロード完了をバックエンドに通知
    const completeResponse = await notifyUploadComplete(itemId, blobName);

    job.status = 'completed';

    if (job.callbacks?.onComplete) {
      job.callbacks.onComplete(itemId, completeResponse.fileUrl);
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      job.status = 'paused';
    } else {
      job.status = 'error';
      job.error = error instanceof Error ? error : new Error('Unknown error');

      if (job.callbacks?.onError) {
        job.callbacks.onError(itemId, job.error);
      }
    }
  }
}

/**
 * チャンク単位でアップロード
 */
async function uploadChunks(job: UploadJob): Promise<void> {
  while (job.uploadedBytes < job.totalBytes) {
    const start = job.uploadedBytes;
    const end = Math.min(start + CHUNK_SIZE, job.totalBytes);
    const chunk = job.file.slice(start, end);

    const response = await fetch(job.uploadUri, {
      method: 'PUT',
      headers: {
        'Content-Length': String(chunk.size),
        'Content-Range': `bytes ${start}-${end - 1}/${job.totalBytes}`,
      },
      body: chunk,
      signal: job.abortController.signal,
    });

    // 308 Resume Incomplete: 次のチャンクへ
    // 200/201: 完了
    if (response.status === 308) {
      const range = response.headers.get('Range');
      if (range) {
        const match = range.match(/bytes=0-(\d+)/);
        if (match) {
          job.uploadedBytes = parseInt(match[1], 10) + 1;
        }
      } else {
        job.uploadedBytes = end;
      }
    } else if (response.ok) {
      job.uploadedBytes = job.totalBytes;
    } else {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    // 進捗コールバック（現在のjob.callbacksを参照する）
    if (job.callbacks?.onProgress) {
      job.callbacks.onProgress(job.uploadedBytes, job.totalBytes);
    }
  }
}

/**
 * アップロード完了をバックエンドに通知
 */
async function notifyUploadComplete(
  itemId: string,
  blobName: string,
): Promise<VideoUploadCompleteResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(getApiUrl('/api/video/complete-upload'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      itemId,
      blobName,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to complete upload: ${response.status}`);
  }

  return await response.json();
}

/**
 * アップロードを一時停止
 */
export function pauseUpload(itemId: string): void {
  const job = uploadJobs.get(itemId);
  if (job && job.status === 'uploading') {
    job.abortController.abort();
    job.status = 'paused';
  }
}

/**
 * アップロードを再開（GCS Resumable Upload対応）
 */
export async function resumeUpload(
  itemId: string,
  file: File,
  uploadResumable: UploadResumable,
  callbacks?: UploadCallbacks,
): Promise<void> {
  // ファイルの照合
  if (
    file.name !== uploadResumable.fileName ||
    file.size !== uploadResumable.fileSize ||
    file.lastModified !== uploadResumable.lastModified
  ) {
    throw new Error('ファイルが一致しません。同じファイルを選択してください。');
  }

  // GCSに現在のアップロード状態を問い合わせ
  const currentBytes = await queryUploadStatus(uploadResumable.uploadUri);

  // アップロードを再開
  await startVideoUpload(
    itemId,
    file,
    uploadResumable.uploadUri,
    '', // blobNameはすでに保存されている
    currentBytes,
    callbacks,
  );
}

/**
 * GCS Resumable Uploadの状態を問い合わせ
 */
async function queryUploadStatus(uploadUri: string): Promise<number> {
  const response = await fetch(uploadUri, {
    method: 'PUT',
    headers: {
      'Content-Length': '0',
      'Content-Range': 'bytes */*',
    },
  });

  if (response.status === 308) {
    const range = response.headers.get('Range');
    if (range) {
      const match = range.match(/bytes=0-(\d+)/);
      if (match) {
        return parseInt(match[1], 10) + 1;
      }
    }
    return 0;
  } else if (response.ok) {
    return -1;
  } else if (response.status === 404) {
    throw new Error('アップロードセッションが期限切れです。最初からアップロードし直してください。');
  }

  throw new Error(`Failed to query upload status: ${response.status}`);
}

/**
 * アップロードをキャンセル
 */
export async function cancelUpload(itemId: string): Promise<void> {
  const job = uploadJobs.get(itemId);
  if (job) {
    if (job.status === 'uploading') {
      job.abortController.abort();
    }
    uploadJobs.delete(itemId);
  }
}

/**
 * UploadResumable情報を作成
 */
export function createUploadResumable(
  file: File,
  uploadUri: string,
  uploadedBytes: number,
): UploadResumable {
  return {
    uploadUri,
    totalBytes: file.size,
    uploadedBytes,
    fileName: file.name,
    fileSize: file.size,
    lastModified: file.lastModified,
    startedAt: new Date().toISOString(),
  };
}

/**
 * サポートされている動画形式かチェック
 */
export function isValidVideoFile(file: File): boolean {
  const validTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
  const validExtensions = ['.mp4', '.mov', '.webm'];

  const hasValidType = validTypes.includes(file.type);
  const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

  return hasValidType || hasValidExtension;
}

/**
 * ファイルサイズのバリデーション (最大20GB)
 */
export function isValidFileSize(file: File): boolean {
  const maxSize = 20 * 1024 * 1024 * 1024; // 20GB
  return file.size <= maxSize;
}

/**
 * ファイルサイズを人間が読める形式に変換
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

/**
 * 秒数を時間形式に変換 (HH:MM:SS)
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

// ==================== リアルタイムステータス監視 ====================

/**
 * 処理ステータスの型定義
 */
export interface ProcessingStatus {
  itemId: string;
  stage: 'pending' | 'uploaded' | 'transcribing' | 'extracting' | 'completed' | 'error';
  progress: number;
  message: string;
  error?: string | null;
  fileUrl?: string;
  blobName?: string;
  fileSize?: number;
  transcriptBlobName?: string;
  durationSec?: number;
  asrProvider?: string;
  asrModel?: string;
  totalFacts?: number;
  resultBlobName?: string;
  updatedAt?: string;
}

/**
 * 処理結果の型定義
 */
export interface ProcessingResultFact {
  factId: string;
  claim: string;
  /** 主張の根拠となる原文からの引用 */
  quote: string;
  /** @deprecated 旧フィールド名。保存済みデータ用 */
  evidence?: string;
  verification_strategy: 'internal_knowledge' | 'google_search';
  start_sec: number;
  end_sec?: number;
  segment_ids?: string[];
  confidence: number;
}

export interface ProcessingResult {
  itemId: string;
  facts: ProcessingResultFact[];
  totalFacts: number;
  completedAt: string;
}

// 監視コールバック型
export type ProcessingStatusCallback = (status: ProcessingStatus) => void;
export type ProcessingCompleteCallback = (itemId: string, result: ProcessingResult) => void;
export type ProcessingErrorCallback = (itemId: string, error: string) => void;

// 監視コールバックのセット
export interface ProcessingCallbacks {
  onStatusUpdate?: ProcessingStatusCallback;
  onComplete?: ProcessingCompleteCallback;
  onError?: ProcessingErrorCallback;
}

// アクティブなリスナーごとのコールバックを管理
const statusCallbacks: Map<string, ProcessingCallbacks> = new Map();

// アクティブなリスナー解除関数
const statusUnsubscribers: Map<string, () => void> = new Map();

/**
 * 処理監視コールバックを更新
 */
export function updateProcessingCallbacks(
  itemId: string,
  callbacks: ProcessingCallbacks | null,
): void {
  if (callbacks) {
    statusCallbacks.set(itemId, callbacks);
  } else {
    statusCallbacks.delete(itemId);
  }
}

/**
 * 処理結果を取得
 */
export async function getProcessingResult(itemId: string): Promise<ProcessingResult> {
  const headers: Record<string, string> = {};

  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(getApiUrl(`/api/video/processing-result/${itemId}`), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get processing result: ${response.status}`);
  }

  return await response.json();
}

/**
 * 処理ステータスの監視を開始（Firestoreリスナー）
 * @param itemId アイテムID
 * @param callbacks コールバック
 */
export function startStatusListener(itemId: string, callbacks?: ProcessingCallbacks): void {
  // 既存の監視があれば停止
  stopStatusListener(itemId);

  if (callbacks) {
    statusCallbacks.set(itemId, callbacks);
  }

  console.log(`🔄 Firestoreステータス監視開始: ${itemId}`);

  const unsubscribe = listenToProcessingStatus(itemId, async (status: ProcessingStatus) => {
    try {
      const currentCallbacks = statusCallbacks.get(itemId);

      // ステータスコールバックを呼び出し
      if (currentCallbacks?.onStatusUpdate) {
        currentCallbacks.onStatusUpdate(status);
      }

      // 処理完了時
      if (status.stage === 'completed') {
        console.log(`✅ 処理完了検出: ${itemId}`);
        stopStatusListener(itemId);

        if ((status as any).facts) {
          if (currentCallbacks?.onComplete) {
            currentCallbacks.onComplete(itemId, {
              itemId: status.itemId,
              facts: (status as any).facts,
              totalFacts: (status as any).totalFacts || 0,
              completedAt: (status as any).completedAt || new Date().toISOString(),
            });
          }
        } else {
          try {
            const result = await getProcessingResult(itemId);
            if (currentCallbacks?.onComplete) {
              currentCallbacks.onComplete(itemId, result);
            }
          } catch (resultError) {
            console.error(`❌ 処理結果取得エラー: ${resultError}`);
            if (currentCallbacks?.onError) {
              currentCallbacks.onError(itemId, '処理結果の取得に失敗しました');
            }
          }
        }
      }

      // エラー時
      if (status.stage === 'error') {
        console.log(`❌ 処理エラー検出: ${itemId}`);
        stopStatusListener(itemId);

        if (currentCallbacks?.onError) {
          currentCallbacks.onError(itemId, status.error || '処理中にエラーが発生しました');
        }
      }
    } catch (error) {
      console.error(`❌ ステータス処理エラー: ${error}`);
    }
  });

  statusUnsubscribers.set(itemId, unsubscribe);
}

/**
 * ステータス監視を停止
 */
export function stopStatusListener(itemId: string): void {
  const unsubscribe = statusUnsubscribers.get(itemId);
  if (unsubscribe) {
    unsubscribe();
    statusUnsubscribers.delete(itemId);
    statusCallbacks.delete(itemId); // コールバックも削除
    console.log(`⏹️ ステータス監視停止: ${itemId}`);
  }
}

/**
 * すべての監視を停止
 */
export function stopAllStatusListeners(): void {
  for (const [itemId, unsubscribe] of statusUnsubscribers) {
    unsubscribe();
    console.log(`⏹️ ステータス監視停止: ${itemId}`);
  }
  statusUnsubscribers.clear();
  statusCallbacks.clear();
}

/**
 * 監視がアクティブかどうか
 */
export function isStatusListenerActive(itemId: string): boolean {
  return statusUnsubscribers.has(itemId);
}
